import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import api from '../services/api';
import { BACKEND_URL } from '../config';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';
import { Printer, Trash2, Wallet, RotateCcw, FileText } from 'lucide-react';

const BusinessReturnView = ({ projectMode = false }) => {
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  const routeBase = useWorkspaceBasePath();

  const { id } = useParams();
  const printRef = useRef();
  const [returnData, setReturnData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const [retRes, setRes] = await Promise.all([
        api.get(`${apiBase}/returns/${id}`),
        api.get(`${apiBase}/settings`)
      ]);
      setReturnData(retRes.data);
      setSettings(setRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  const handleDelete = async () => {
    if (!await confirm({ message: 'Supprimer cet avoir ?\n\nLe stock sera re-déduit et le crédit client annulé.', type: 'danger' })) return;
    try {
      await api.delete(`${apiBase}/returns/${id}`);
      window.location.href = `${routeBase}/returns`;
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!returnData) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-center">
        <h2 className="text-xl font-bold text-gray-700">Avoir non trouvé</h2>
        <Link to={`${routeBase}/returns`} className="text-indigo-600 hover:underline mt-4 inline-block">← Retour à la liste</Link>
      </div>
    );
  }

  const r = returnData;

  return (
    <div className="max-w-5xl mx-auto p-4 lg:p-6">
      {/* Actions header */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <Link to={`${routeBase}/returns`} className="flex items-center gap-2 text-gray-500 hover:text-gray-700">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Liste des avoirs
        </Link>
        <div className="flex items-center gap-3">
          <button onClick={handlePrint} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2">
            <Printer size={16} /> Imprimer
          </button>
          <button onClick={handleDelete} className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition flex items-center gap-2">
            <Trash2 size={16} /> Supprimer
          </button>
        </div>
      </div>

      {/* Printable content */}
      <div ref={printRef}>
        <style dangerouslySetInnerHTML={{ __html: `
          @media print {
            @page { size: A4; margin: 15mm; }
            body { print-color-adjust: exact; -webkit-print-color-adjust: exact; font-family: system-ui, sans-serif; }
          }
        `}} />

        <div className="bg-white rounded-2xl shadow-sm border p-8">
          {/* Header entreprise */}
          <div className="flex justify-between items-start mb-8 pb-6 border-b">
            <div>
              {settings?.logo && (
                <img src={`${BACKEND_URL}/${settings.logo}`} alt="Logo" className="h-16 mb-3 object-contain" />
              )}
              <h2 className="text-xl font-bold text-gray-800">{settings?.nom_entreprise || 'Mon Entreprise'}</h2>
              {settings?.adresse && <p className="text-sm text-gray-500">{settings.adresse}</p>}
              {settings?.telephone && <p className="text-sm text-gray-500">Tél: {settings.telephone}</p>}
              {settings?.email && <p className="text-sm text-gray-500">{settings.email}</p>}
              {settings?.numero_registre && <p className="text-sm text-gray-500">RCCM: {settings.numero_registre}</p>}
              {settings?.numero_fiscal && <p className="text-sm text-gray-500">NIF: {settings.numero_fiscal}</p>}
            </div>
            <div className="text-right">
              <h1 className="text-3xl font-bold text-red-600">AVOIR</h1>
              <p className="text-lg font-semibold text-gray-700 mt-1">{r.numero_avoir}</p>
              <p className="text-sm text-gray-500 mt-2">Date : {new Date(r.date_retour).toLocaleDateString('fr-FR')}</p>
              <p className="text-sm text-gray-500">Facture : {r.numero_facture}</p>
              <div className="mt-2">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${r.type_remboursement === 'credit_client' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                  {r.type_remboursement === 'credit_client' ? <><Wallet size={14} className="inline" /> Crédit client</> : <><RotateCcw size={14} className="inline" /> Remboursement direct</>}
                </span>
              </div>
            </div>
          </div>

          {/* Client */}
          {r.client_nom && (
            <div className="mb-6 p-4 bg-gray-50 rounded-xl">
              <h3 className="text-sm font-semibold text-gray-500 mb-1">CLIENT</h3>
              <p className="font-bold text-gray-800">{r.client_nom}</p>
              {r.client_adresse && <p className="text-sm text-gray-600">{r.client_adresse}</p>}
              {r.client_telephone && <p className="text-sm text-gray-600">Tél: {r.client_telephone}</p>}
              {r.client_email && <p className="text-sm text-gray-600">{r.client_email}</p>}
            </div>
          )}

          {/* Motif */}
          {r.motif && (
            <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-100">
              <h3 className="text-sm font-semibold text-amber-700 mb-1">MOTIF DU RETOUR</h3>
              <p className="text-gray-700">{r.motif}</p>
            </div>
          )}

          {/* Tableau articles */}
          <table className="w-full mb-6">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left p-3 text-sm font-semibold text-gray-600 rounded-tl-lg">Description</th>
                <th className="text-center p-3 text-sm font-semibold text-gray-600">Qté retournée</th>
                <th className="text-right p-3 text-sm font-semibold text-gray-600">Prix unit.</th>
                <th className="text-right p-3 text-sm font-semibold text-gray-600 rounded-tr-lg">Total</th>
              </tr>
            </thead>
            <tbody>
              {r.items?.map((item, idx) => (
                <tr key={idx} className="border-b border-gray-100">
                  <td className="p-3 text-gray-800">
                    <div className="font-medium">{item.description}</div>
                    {item.code_produit && <div className="text-xs text-gray-400">Code: {item.code_produit}</div>}
                  </td>
                  <td className="p-3 text-center text-gray-700">{item.quantite_retournee}</td>
                  <td className="p-3 text-right text-gray-700">{parseFloat(item.prix_unitaire).toLocaleString()} XOF</td>
                  <td className="p-3 text-right font-semibold text-gray-800">{parseFloat(item.total).toLocaleString()} XOF</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Total */}
          <div className="flex justify-end mb-6">
            <div className="w-64 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Sous-total</span>
                <span className="text-gray-700">{parseFloat(r.sous_total).toLocaleString()} XOF</span>
              </div>
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                <span className="text-gray-800">Total Avoir</span>
                <span className="text-red-600">{parseFloat(r.total).toLocaleString()} XOF</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {r.notes && (
            <div className="p-4 bg-gray-50 rounded-xl">
              <h3 className="text-sm font-semibold text-gray-500 mb-1">NOTES</h3>
              <p className="text-sm text-gray-600 whitespace-pre-line">{r.notes}</p>
            </div>
          )}

          {/* Signature */}
          <div className="mt-8 pt-6 border-t grid grid-cols-2 gap-8">
            <div>
              <p className="text-sm font-semibold text-gray-500 mb-8">Signature de l'entreprise</p>
              <div className="border-b border-gray-300 w-48"></div>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 mb-8">Signature du client</p>
              <div className="border-b border-gray-300 w-48"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Lien vers la facture */}
      <div className="mt-6 text-center print:hidden">
        <Link to={`${routeBase}/invoices/${r.invoice_id}`} className="text-indigo-600 hover:underline">
          <FileText size={14} className="inline" /> Voir la facture {r.numero_facture}
        </Link>
      </div>
    </div>
  );
};

export default BusinessReturnView;
